import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  Monitor, 
  ShoppingCart, 
  Target, 
  Globe, 
  Search, 
  CreditCard, 
  Truck, 
  Settings,
  Palette,
  Code,
  Smartphone,
  Wrench,
  ArrowRight,
  CheckCircle2,
  Sparkles
} from "lucide-react";
import { Button } from "@/components/ui/button";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.2 }
  }
};

const services = [
  {
    id: "weboldal",
    icon: Monitor,
    title: "Weboldalkészítés",
    subtitle: "Céges weboldalak és landing page-ek",
    description: "Egyedi, konverziófókuszú weboldalak készítése, amelyek nem csak szépek, de valóban hoznak eredményt.",
    features: [
      { icon: Palette, text: "Egyedi, prémium design" },
      { icon: Smartphone, text: "Teljes reszponzivitás" },
      { icon: Search, text: "SEO alapoptimalizálás" },
      { icon: Globe, text: "Többnyelvű támogatás" },
      { icon: Code, text: "Modern technológiák" },
      { icon: Wrench, text: "Könnyű karbantartás" },
    ],
    price: "199.000 Ft-tól"
  },
  {
    id: "webshop",
    icon: ShoppingCart,
    title: "Webshop készítés",
    subtitle: "E-commerce megoldások vállalkozásoknak",
    description: "Komplett webáruház készítés kisebb vállalkozásoknak, egyszerű kezelhetőséggel és teljes fizetési integrációval.",
    features: [
      { icon: ShoppingCart, text: "50-300 termék kezelése" },
      { icon: CreditCard, text: "Online fizetés (SimplePay, Barion)" },
      { icon: Truck, text: "Szállítási integráció" },
      { icon: Settings, text: "Egyszerű admin felület" },
      { icon: Search, text: "Termékkereső és szűrők" },
      { icon: Smartphone, text: "Mobilbarát vásárlás" },
    ],
    price: "499.000 Ft-tól"
  },
  {
    id: "landing",
    icon: Target,
    title: "Landing Page",
    subtitle: "Céloldalak kampányokhoz",
    description: "Értékesítésre optimalizált céloldalak, amelyek egyetlen célt szolgálnak: konverziót generálni.",
    features: [
      { icon: Target, text: "Konverziófókuszú design" },
      { icon: Palette, text: "A/B tesztelésre optimalizált" },
      { icon: Smartphone, text: "Gyors betöltés" },
      { icon: Search, text: "SEO és hirdetés kompatibilis" },
      { icon: Code, text: "Analitika integráció" },
      { icon: CheckCircle2, text: "CTA optimalizálás" },
    ],
    price: "149.000 Ft-tól"
  }
];

// Hero Section
const PageHero = () => {
  return (
    <section className="pt-24 sm:pt-32 pb-12 sm:pb-20 bg-[#030303] relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 right-1/4 w-64 sm:w-96 h-64 sm:h-96 bg-[#D4AF37] rounded-full filter blur-[100px] sm:blur-[150px]" />
      </div>
      
      <div className="container mx-auto px-5 sm:px-6 md:px-8 max-w-7xl relative z-10">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="max-w-3xl"
        >
          <motion.p variants={fadeInUp} className="text-[0.6rem] sm:text-[0.65rem] text-[#D4AF37] tracking-[0.2em] sm:tracking-[0.3em] uppercase mb-3 sm:mb-4">
            Szolgáltatások
          </motion.p>
          <motion.h1 variants={fadeInUp} className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-white mb-4 sm:mb-6">
            Minden, amire{" "}
            <span className="italic-accent text-gold-gradient">szükséged</span>{" "}
            van
          </motion.h1>
          <motion.div variants={fadeInUp} className="gold-line mb-6 sm:mb-8" />
          <motion.p variants={fadeInUp} className="text-zinc-400 text-sm sm:text-base md:text-lg leading-relaxed">
            Weboldalak, webshopok és landing page-ek készítése. Minden projekt egyedi, 
            minden megoldás a Te céljaidra szabva.
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
};

// Service Card Component
const ServiceCard = ({ service, index }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.2 }}
      className="group"
      data-testid={`service-detail-${service.id}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="premium-card p-5 sm:p-6 md:p-8 lg:p-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-10">
          {/* Left - Info */}
          <div>
            <div className="service-icon mb-4 sm:mb-6">
              <service.icon className="w-5 h-5 sm:w-6 sm:h-6" strokeWidth={1.5} />
            </div>
            <p className="text-[0.6rem] sm:text-[0.65rem] text-[#D4AF37] tracking-[0.15em] sm:tracking-[0.2em] uppercase mb-1 sm:mb-2">
              {service.subtitle}
            </p>
            <h2 className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl md:text-4xl text-white mb-3 sm:mb-4">
              {service.title}
            </h2>
            <p className="text-zinc-400 text-sm sm:text-base leading-relaxed mb-4 sm:mb-6">
              {service.description}
            </p>
            <div className="flex items-baseline gap-2 mb-6 sm:mb-8">
              <span className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl text-[#D4AF37]">
                {service.price}
              </span>
            </div>
            <Link to="/kapcsolat">
              <Button className="btn-gold w-full sm:w-auto">
                Ajánlatot kérek
                <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
              </Button>
            </Link>
          </div>

          {/* Right - Features */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
            {service.features.map((feature, idx) => (
              <div 
                key={idx}
                className="flex items-start gap-2 sm:gap-3 p-3 sm:p-4 bg-white/[0.02] border border-white/5 rounded"
              >
                <feature.icon className="w-4 h-4 sm:w-5 sm:h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                <span className="text-zinc-300 text-xs sm:text-sm">{feature.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Hover CTA - Tervezd meg a sajátod - Hidden on mobile */}
      <AnimatePresence>
        {isHovered && (
          <motion.div
            initial={{ opacity: 0, y: -10, height: 0 }}
            animate={{ opacity: 1, y: 0, height: "auto" }}
            exit={{ opacity: 0, y: -10, height: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="overflow-hidden hidden sm:block"
          >
            <Link to="/tervezo">
              <div className="mt-4 py-4 sm:py-5 px-6 sm:px-8 border border-[#D4AF37]/30 bg-[#D4AF37]/5 flex items-center justify-between hover:bg-[#D4AF37]/10 hover:border-[#D4AF37]/50 transition-all duration-300">
                <div className="flex items-center gap-3 sm:gap-4">
                  <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-[#D4AF37]" strokeWidth={1.5} />
                  <div>
                    <span className="text-white font-medium text-sm sm:text-base">Tervezd meg a sajátod</span>
                    <span className="text-zinc-500 text-xs sm:text-sm ml-2 sm:ml-3">Lépésről lépésre, egyszerűen</span>
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 text-[#D4AF37]" strokeWidth={1.5} />
              </div>
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Process Section
const ProcessSection = () => {
  const steps = [
    { num: "01", title: "Konzultáció", desc: "Megértjük a céljaidat és igényeidet." },
    { num: "02", title: "Tervezés", desc: "Struktúra és design kidolgozása." },
    { num: "03", title: "Fejlesztés", desc: "Az oldal megvalósítása." },
    { num: "04", title: "Élesítés", desc: "Publikálás és támogatás." },
  ];

  return (
    <section className="py-16 sm:py-24 md:py-32 bg-[#050505]" data-testid="process-section">
      <div className="container mx-auto px-5 sm:px-6 md:px-8 max-w-7xl">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="text-center mb-10 sm:mb-16">
            <p className="text-[0.6rem] sm:text-[0.65rem] text-[#D4AF37] tracking-[0.2em] sm:tracking-[0.3em] uppercase mb-3 sm:mb-4">Folyamat</p>
            <h2 className="font-['Cormorant_Garamond'] text-2xl sm:text-4xl md:text-5xl text-white mb-3 sm:mb-4">
              Hogyan dolgozom?
            </h2>
            <div className="gold-line-center mt-4 sm:mt-6" />
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 md:gap-8">
            {steps.map((step, idx) => (
              <motion.div
                key={idx}
                variants={fadeInUp}
                className="text-center"
              >
                <div className="process-number mx-auto mb-4 sm:mb-6">{step.num}</div>
                <h3 className="font-['Cormorant_Garamond'] text-lg sm:text-xl text-white mb-1 sm:mb-2">
                  {step.title}
                </h3>
                <p className="text-zinc-500 text-xs sm:text-sm">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Main Services Page
export default function ServicesPage() {
  return (
    <div>
      <PageHero />
      
      <section className="py-12 sm:py-16 md:py-20 bg-[#030303]">
        <div className="container mx-auto px-5 sm:px-6 md:px-8 max-w-7xl space-y-10 sm:space-y-16 md:space-y-20">
          {services.map((service, idx) => (
            <ServiceCard key={service.id} service={service} index={idx} />
          ))}
        </div>
      </section>

      <ProcessSection />

      {/* CTA */}
      <section className="py-16 sm:py-20 md:py-24 bg-[#030303]">
        <div className="container mx-auto px-5 sm:px-6 md:px-8 max-w-3xl text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 variants={fadeInUp} className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl md:text-4xl text-white mb-4 sm:mb-6">
              Kérdésed van?
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-zinc-400 text-sm sm:text-base mb-6 sm:mb-8">
              Foglalj egy ingyenes konzultációt, és beszéljük meg a projekted részleteit.
            </motion.p>
            <motion.div variants={fadeInUp}>
              <Link to="/kapcsolat">
                <Button className="btn-gold w-full sm:w-auto">
                  Kapcsolatfelvétel
                  <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
